import { memo, useMemo } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import NoContentCard from "src/reusable/Sidebar/NoContentCard";
import StepProgressList, {
  isProgressStep,
  type ProgressItem,
} from "src/reusable/components/StepProgressList";
import { SideBarScroll } from "src/views/assistant/components/ui/SideBarScroll";
import type { ProposalGenerationPlan } from "../../services/ProposalGenerationServices";

interface PlanTabProps {
  plans: ProposalGenerationPlan[];
}

type ParsedLine =
  | { kind: "step"; checked: boolean; text: string }
  | { kind: "text"; text: string };

const CHECKBOX_RE = /^\s*[-*]\s*\[([ xX])\]\s+(.*)$/;

const parsePlanContent = (content: string): ParsedLine[] => {
  return content.split(/\r?\n/).map((line) => {
    const match = line.match(CHECKBOX_RE);
    if (match) {
      return {
        kind: "step" as const,
        checked: match[1].toLowerCase() === "x",
        text: match[2],
      };
    }
    return { kind: "text" as const, text: line };
  });
};

const STATUS_COLOR: Record<
  ProposalGenerationPlan["status"],
  "default" | "primary" | "success" | "error"
> = {
  created: "default",
  in_progress: "primary",
  completed: "success",
  failed: "error",
};

const STATUS_LABEL: Record<ProposalGenerationPlan["status"], string> = {
  created: "Created",
  in_progress: "In Progress",
  completed: "Completed",
  failed: "Failed",
};

const PlanCard = ({ plan }: { plan: ProposalGenerationPlan }) => {
  // Blank lines are dropped so notes do not render as empty gaps.
  const items = useMemo(
    () =>
      parsePlanContent(plan.content).flatMap((line, idx): ProgressItem[] => {
        if (line.kind === "step") {
          return [
            {
              key: String(idx),
              label: line.text,
              status: line.checked ? "complete" : "pending",
            },
          ];
        }
        return line.text.trim()
          ? [{ kind: "note", key: String(idx), text: line.text }]
          : [];
      }),
    [plan.content],
  );

  const steps = items.filter(isProgressStep);
  const allStepsDone =
    steps.length > 0 && steps.every((step) => step.status === "complete");
  const displayStatus: ProposalGenerationPlan["status"] =
    plan.status === "failed"
      ? "failed"
      : allStepsDone
        ? "completed"
        : plan.status;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
        mb: 1.5,
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ minWidth: 0 }}
          noWrap
        >
          {plan.name}
        </Typography>
        <Chip
          label={STATUS_LABEL[displayStatus]}
          size="small"
          color={STATUS_COLOR[displayStatus]}
          variant={displayStatus === "completed" ? "filled" : "outlined"}
          sx={{ flexShrink: 0 }}
        />
      </Stack>

      <StepProgressList items={items} showPercent strikeCompleted />

      {displayStatus === "completed" && plan.result_summary && (
        <Box
          sx={{
            mt: 1.5,
            p: 1,
            borderRadius: 1,
            bgcolor: "action.hover",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{
              display: "block",
              mb: 0.5,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Summary
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {plan.result_summary}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const PlanTab = memo(({ plans }: PlanTabProps) => {
  if (!plans.length) {
    return (
      <NoContentCard
        title="No plan yet"
        description="When the agent creates a plan to break this task into steps, it will appear here and update as the agent works through it."
      />
    );
  }

  return (
    <SideBarScroll>
      <Box sx={{ px: 1, pt: 0.5 }}>
        {plans.map((plan) => (
          <PlanCard key={plan.plan_id} plan={plan} />
        ))}
      </Box>
    </SideBarScroll>
  );
});

PlanTab.displayName = "PlanTab";

export default PlanTab;
